/**
 * lensCoordinateSpaces.js ✦ iter202
 * ─────────────────────────────────────────────────────────────
 * Lens Notes is the only surface in the platform that
 * simultaneously juggles FIVE coordinate spaces:
 *
 *   1. VIDEO space        — vw × vh (whatever the sensor delivers,
 *                            typically landscape on most cameras)
 *   2. CANVAS space       — PORTRAIT_W × PORTRAIT_H (1080 × 1920),
 *                            single source of truth for preview +
 *                            recorder + export
 *   3. DISPLAY space      — visible composite canvas's clientWidth ×
 *                            clientHeight (varies by viewport)
 *   4. STROKE space       — stored in the overlay canvas at
 *                            PORTRAIT_W × PORTRAIT_H, pre-mirrored on
 *                            X so the compose-time flip lands them
 *                            back at the touch position
 *   5. EXPORT space       — same as CANVAS space (the composite IS
 *                            the MediaRecorder source)
 *
 * Under `mirror-export` semantics (the `lens` profile's choice),
 * both the video frame AND the overlay are X-flipped at compose
 * time inside drawFrame. The full coordinate journey for a single
 * pen stroke:
 *
 *   touch in DISPLAY space
 *      → pointerPos() un-flips X (rect.right - clientX)
 *      → stored in STROKE space at flipped X in OVERLAY
 *      → drawFrame composites OVERLAY into CANVAS with X-flip
 *      → MediaRecorder serialises CANVAS → EXPORT space
 *      → guest sees a video where their writing reads correctly
 *        AND is positioned exactly where their finger landed
 *
 * Two invariants this module exists to enforce:
 *
 *   I1. CANVAS dimensions are LOCKED at PORTRAIT_W × PORTRAIT_H
 *       regardless of sensor dimensions. No surface may inherit
 *       the raw camera resolution.
 *   I2. The composite canvas has NO CSS transform — visible pixels
 *       ARE export pixels. WYSIWYG byte-identical.
 *
 * If a future refactor breaks either invariant, the lens
 * coordinate journey de-syncs and handwriting drifts off-frame
 * during export. The contract tests in
 * test_lens_camera_pipeline_iter202.py guard both invariants.
 */

export const LENS_CANVAS_W = 1080;
export const LENS_CANVAS_H = 1920;

/**
 * Translate a DOM pointer event into STROKE-space coordinates,
 * un-flipping X so the compose-time X-flip in drawFrame lands the
 * stroke at the touch position. Pure function — exported so the
 * /debug/composition-language route can show the live mapping
 * without re-implementing the math.
 */
export function pointerEventToStrokeSpace(e, hitRect) {
  if (!hitRect) return { x: 0, y: 0, pressure: 0.5 };
  const xScale = LENS_CANVAS_W / hitRect.width;
  const yScale = LENS_CANVAS_H / hitRect.height;
  return {
    x: (hitRect.right - e.clientX) * xScale,
    y: (e.clientY - hitRect.top) * yScale,
    pressure: (e.pressure && e.pressure > 0) ? e.pressure : 0.5,
  };
}

/**
 * Compute CONTAIN-fit destination rect for a video frame inside the
 * portrait composite. Preserves the entire sensor FOV inside the
 * portrait canvas — no zoom, no crop, calm black bands where the
 * sensor aspect doesn't match.
 *
 * Returns { dx, dy, dw, dh } in CANVAS space.
 */
export function containFitVideoIntoCanvas(vw, vh) {
  if (!vw || !vh) return { dx: 0, dy: 0, dw: 0, dh: 0 };
  const scale = Math.min(LENS_CANVAS_W / vw, LENS_CANVAS_H / vh);
  const dw = vw * scale;
  const dh = vh * scale;
  return {
    dx: (LENS_CANVAS_W - dw) / 2,
    dy: (LENS_CANVAS_H - dh) / 2,
    dw,
    dh,
  };
}

/**
 * Cheap sanity check — runs in development to verify a composite
 * canvas hasn't been resized away from its locked dimensions. Used
 * by the /debug/composition-language route to surface drift.
 */
export function assertCanvasInvariants(canvas) {
  if (!canvas) return { ok: false, reason: 'canvas missing' };
  if (canvas.width !== LENS_CANVAS_W) {
    return { ok: false, reason: `canvas.width=${canvas.width} expected ${LENS_CANVAS_W}` };
  }
  if (canvas.height !== LENS_CANVAS_H) {
    return { ok: false, reason: `canvas.height=${canvas.height} expected ${LENS_CANVAS_H}` };
  }
  // No CSS transform — visible pixels must equal export pixels.
  const cs = (typeof window !== 'undefined') ? window.getComputedStyle(canvas) : null;
  if (cs && cs.transform && cs.transform !== 'none') {
    return { ok: false, reason: `canvas carries CSS transform "${cs.transform}" — must be "none"` };
  }
  return { ok: true };
}
